import { ApiBoardImageCopy } from "@/api";
import { canv } from "@/canv/global";
import { logException } from "@/canv/utils";
import { EmbedWidget } from "@/elements/embed";
import { checkLinkForEmbed } from "@/elements/embed/utils";
import { textWidget } from "@/elements/text/text";
import { Group } from "@/group";
import { showSnackbar } from "@/snackbar/snackbar";
import { selectionManager } from "@/tools/cursorTool";
import type { BoardEntity } from "../types";
import { processDataFromClipboard } from "./clipboard";
import { sortByIndexByParent } from "./common";
import { loadFiles } from "./image";

export function pasteEmbed(src: string) {
    const newEmbedWidget = new EmbedWidget(src);
    const newGroup = new Group(true);

    newEmbedWidget.setParent(newGroup);

    const cursorPoint = canv.actionManager.getActionPoint();
    const positionX = cursorPoint.x - newGroup.startX - newGroup.width / 2;
    const positionY = cursorPoint.y - newGroup.startY - newGroup.height / 2;
    newGroup.translateGroup(positionX, positionY);

    canv.canvasController.addGroup(newGroup);
    canv.eventManager.openRecord();
    canv.eventManager.createBuildEvent("create", newGroup);
    canv.eventManager.closeRecord();

    canv.toolBox.TryGetToolByName("cursorTool")?.setSelectedAndInteraction(newGroup);
    canv.redraw();

    return newGroup;
}

export function pasteText(text: string) {
    const pt = canv.actionManager.getActionPoint();

    const creationStartX = pt.x;
    const creationStartY = pt.y;

    const newTextItem = new textWidget(creationStartX, creationStartY);
    const newGroup = new Group(true);
    newTextItem.setParent(newGroup);
    newTextItem.setText(text);

    const cursorPoint = canv.actionManager.getActionPoint();
    const positionX = cursorPoint.x - newGroup.startX - newGroup.width / 2;
    const positionY = cursorPoint.y - newGroup.startY - newGroup.height / 2;
    newGroup.translateGroup(positionX, positionY);

    canv.canvasController.addGroup(newGroup);
    canv.eventManager.openRecord();
    canv.eventManager.createBuildEvent("create", newGroup);
    canv.eventManager.closeRecord();

    canv.toolBox.TryGetToolByName("cursorTool")?.setSelectedAndInteraction(newGroup);
    canv.redraw();

    return newGroup;
}

export function pasteHtmlText(data) {
    const pt = canv.actionManager.getActionPoint();

    const creationStartX = pt.x;
    const creationStartY = pt.y;

    const newTextItem = new textWidget(creationStartX, creationStartY);
    const newGroup = new Group(true);
    newTextItem.setParent(newGroup);
    newTextItem.setHtmlText(data);

    const cursorPoint = canv.actionManager.getActionPoint();
    const positionX = cursorPoint.x - newGroup.startX - newGroup.width / 2;
    const positionY = cursorPoint.y - newGroup.startY - newGroup.height / 2;
    newGroup.translateGroup(positionX, positionY);

    canv.canvasController.addGroup(newGroup);
    canv.eventManager.openRecord();
    canv.eventManager.createBuildEvent("create", newGroup);
    canv.eventManager.closeRecord();

    canv.toolBox.TryGetToolByName("cursorTool")?.setSelectedAndInteraction(newGroup);
    canv.redraw();

    return newGroup;
}

type Info = {
    sourceBoardUid: string;
    sourceBoardAccessToken: string;
};

type PastePosition = {
    x: number;
    y: number;
};

/**
 *
 * @param {Array} dumps
 * @param {object} info
 * @param {string} info.sourceBoardUid
 * @param {string} info.sourceBoardAccessToken
 * @param {object} [pastePosition]
 * @param {number} pastePosition.x
 * @param {number} pastePosition.y
 * @returns
 */
export async function pasteGroups(
    dumps: JsonObj["dumps"],
    info: Info,
    pastePosition?: PastePosition,
) {
    try {
        if (dumps.length === 0) {
            return;
        }

        const { sourceBoardUid, sourceBoardAccessToken } = info;

        if (!sourceBoardUid) {
            // такого не должно происходить
            console.log("ошибка при вставке");
            return;
        }

        const targetBoardUid = canv.getInitDataProp("boardUID");
        const targetBoardAccessToken = canv.getInitDataProp("boardAccessToken");
        const isSameBoard = sourceBoardUid && sourceBoardUid === targetBoardUid;

        const cursorPoint = canv.actionManager.getActionPoint();

        const newGroups = dumps.map((item) => {
            const branch = canv.canvasController.createBranch(item)[0];
            branch.regenerateUIDS("", true);
            return branch;
        }) as Group[];

        if (!isSameBoard) {
            // вставка на другую доску - ищем картинки, которые нужно скопировать в s3
            /**
             * @type {Map<string, imageWidget[]>}
             */
            const oldPaths = new Map();
            const imageCollectorFunc = (group) => {
                if (group.isItemWrapper()) {
                    const child = group.onlyChild();
                    if (child) {
                        const isImageOrGif =
                            child.constructorName === "image" || child.constructorName === "gif";
                        const isStickerImage =
                            isImageOrGif && child.src.includes("/static/media/stickers/");

                        if (isImageOrGif && !isStickerImage) {
                            const temp = oldPaths.get(child.src);

                            if (Array.isArray(temp)) {
                                oldPaths.set(child.src, [...temp, child]);
                            } else {
                                oldPaths.set(child.src, [child]);
                            }
                        }
                    }
                } else {
                    group.children.forEach(imageCollectorFunc);
                }
            };

            newGroups.forEach(imageCollectorFunc);

            if (oldPaths.size > 0) {
                // нашли картинки, которые нужно скопировать на текущую доску
                const loadingSnackbar = showSnackbar("Вставляем на доску...", {
                    type: "loading",
                });

                const imagesToCopy = Array.from(oldPaths.keys()).map((oldPath) => ({
                    sourcePath: oldPath,
                }));

                const copiedImagesResponse = await ApiBoardImageCopy({
                    imagesToCopy,
                    info: {
                        sourceBoardUid,
                        sourceBoardAccessToken,
                        targetBoardUid,
                        targetBoardAccessToken,
                    },
                })
                    .then((res) => {
                        return res.data;
                    })
                    .catch((e) => {
                        console.error(e);
                        return [];
                    });

                for (const imageData of copiedImagesResponse) {
                    // если нет path в ответе - значит эта картинка не была скопирована
                    if (!imageData.path) {
                        return;
                    }
                    // находим все виджеты, которые были с этим старым path
                    const widgets = oldPaths.get(imageData.sourcePath);

                    if (!widgets) {
                        return;
                    }

                    // проходимся по каждому и меняем на новый path
                    for (const w of widgets) {
                        w.src = imageData.path;
                    }

                    // удаляем уже обновленные
                    oldPaths.delete(imageData.sourcePath);
                }

                if (oldPaths.size > 0) {
                    // удаляем те, у которых не удалось заменить path
                    oldPaths.forEach((imgWidgets, oldPath) => {
                        for (const w of imgWidgets || []) {
                            const index = newGroups.findIndex((g) => g === w.parent);

                            if (index > -1) {
                                newGroups.splice(index, 1);
                            }
                        }

                        oldPaths.delete(oldPath);
                    });
                }
                loadingSnackbar.close();
            }
        }

        canv.eventManager.openRecord();
        sortByIndexByParent(newGroups);

        for (const group of newGroups) {
            canv.canvasController.addGroup(group);
        }

        sortByIndexByParent(canv.canvasController.items);

        for (const group of newGroups) {
            group.recursiveResyncArrows();
        }

        for (const group of newGroups) {
            group.clearTrailUIDs();
        }

        const newGroupsProps = canv.canvasController.getSelectedProps(newGroups);
        const positionX = pastePosition?.x
            ? pastePosition.x - newGroupsProps.startX
            : cursorPoint.x - newGroupsProps.startX - newGroupsProps.width / 2;
        const positionY = pastePosition?.y
            ? pastePosition.y - newGroupsProps.startY
            : cursorPoint.y - newGroupsProps.startY - newGroupsProps.height / 2;

        for (const group of newGroups) {
            group.translateGroup(positionX, positionY);
            canv.eventManager.createBuildEvent("create", group);
        }

        canv.eventManager.closeRecord();

        // после closeRecord, так как новые группы должны уже быть у других пользователей
        selectionManager.setSelected(newGroups);

        canv.redraw();

        return newGroups;
    } catch (e) {
        logException(e as Error);
    }
}

type JsonObj = {
    isJespoDumps: boolean;
    dumps: BoardEntity[][];
    sourceBoardUid: string;
    sourceBoardAccessToken: string;
};

export async function pasteProccessor() {
    async function textItemProcessor(messageStr: string) {
        let ifJson = false;
        let jsonOb: JsonObj | null = null;

        try {
            jsonOb = JSON.parse(messageStr);
            ifJson = true;
        } catch (e) {}

        if (ifJson && jsonOb !== null && jsonOb.isJespoDumps === true) {
            return pasteGroups(jsonOb.dumps, {
                sourceBoardUid: jsonOb.sourceBoardUid,
                sourceBoardAccessToken: jsonOb.sourceBoardAccessToken,
            });
        }

        if (checkLinkForEmbed(messageStr)) {
            pasteEmbed(messageStr);
        } else {
            return pasteText(messageStr);
        }
    }

    const imageItemProcessor = async (blob: Blob) => {
        const groups = await loadFiles(blob);
        return groups?.[0];
    };

    const res = await processDataFromClipboard({
        textItemProcessor: textItemProcessor,
        imageItemProcessor: imageItemProcessor,
        htmlItemProcessor: pasteHtmlText,
    });

    if (res?.groups.length) {
        selectionManager.setSelected(res.groups);
    }

    return res?.groups || [];
}
